<template>
  <div class="page-wrapper">
    <main-navigation></main-navigation>

    <div class="add-tenant-container">
      <!-- LEFT SECTION - BULK UPLOAD -->
      <div class="info-section">
        <div class="info-card">
          <!-- HEADER -->
          <div class="info-header">
            <div class="icon-wrapper">
              <i class="bi bi-people-fill"></i>
            </div>
            <h1 class="title">Add Tenants</h1>
            <p class="subtitle">Bulk upload or add individually</p>
          </div>

          <!-- TEMPLATE CARD -->
          <div class="template-card">
            <div class="template-icon">
              <i class="bi bi-file-earmark-arrow-down"></i>
            </div>
            <div class="template-content">
              <h5>Download CSV Template</h5>
              <p>Pre-formatted with required columns</p>
            </div>
            <button class="btn-template-download" @click="downloadTemplate">
              <i class="bi bi-download"></i>
              Download
            </button>
          </div>

          <!-- UPLOAD AREA -->
          <div class="upload-section">
            <h5 class="section-title">Upload Tenants CSV</h5>
            
            <div class="upload-card">
              <!-- FILE DROP AREA -->
              <div class="file-drop-zone" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
                <input type="file" ref="fileInput" class="d-none" accept=".csv" @change="handleFile" />
                
                <div v-if="!file" class="drop-content">
                  <div class="drop-icon">
                    <i class="bi bi-cloud-arrow-up"></i>
                  </div>
                  <div>
                    <p class="drop-text">Drop CSV file or <span class="browse-link">browse</span></p>
                    <p class="drop-hint">Supports .csv files only</p>
                  </div>
                </div>
                
                <div v-else class="file-preview-card">
                  <div class="file-icon">
                    <i class="bi bi-file-earmark-spreadsheet"></i>
                  </div>
                  <div class="file-details">
                    <p class="file-name">{{ file.name }}</p>
                    <p class="file-info">
                      {{ formatFileSize(file.size) }} • {{ getRowCount() }} tenants
                    </p>
                  </div>
                  <button class="btn-remove-file" @click.stop="removeFile">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>

              <!-- PREVIEW -->
              <div v-if="filePreview.length > 0" class="preview-section">
                <div class="preview-header">
                  <i class="bi bi-eye"></i>
                  <span>Preview ({{ Math.min(3, filePreview.length) }} of {{ filePreview.length }})</span>
                </div>
                <div class="preview-table">
                  <div v-for="(row, index) in filePreview.slice(0, 3)" :key="index" class="preview-row">
                    <div class="preview-cell">
                      <span class="cell-label">Name</span>
                      <span class="cell-value">{{ row.tenant_name || '—' }}</span>
                    </div>
                    <div class="preview-cell">
                      <span class="cell-label">Phone</span>
                      <span class="cell-value">{{ row.tenant_phone || '—' }}</span>
                    </div>
                    <div class="preview-cell">
                      <span class="cell-label">Id Number</span>
                      <span class="cell-value">{{ row.tenant_idnumber || '—' }}</span>
                    </div>
                    <div class="preview-cell">
                      <span class="cell-label">Gender</span>
                      <span class="badge" :class="getGenderBadgeClass(row.gender)">
                        {{ row.gender || '—' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- UPLOAD BUTTON -->
              <button class="btn-upload-primary" :disabled="loading || !file" @click="uploadTenants">
                <span v-if="loading" class="btn-loading">
                  <span class="spinner"></span>
                  Processing...
                </span>
                <span v-else class="btn-content">
                  <i class="bi bi-cloud-upload"></i>
                  Upload Tenants
                </span>
              </button>
            </div>

            <!-- RESULTS -->
            <div v-if="uploadResult" class="results-container">
              <div class="result-card" :class="uploadResult.success ? 'success' : 'error'">
                <div class="result-icon">
                  <i :class="uploadResult.success ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'"></i>
                </div>
                <div class="result-content">
                  <h6>{{ uploadResult.success ? 'Upload Complete!' : 'Upload Failed' }}</h6>
                  <div class="result-stats">
                    <div class="stat-badge">
                      <span class="stat-value">{{ uploadResult.inserted || 0 }}</span>
                      <span class="stat-label">Added</span>
                    </div>
                    <div class="stat-badge">
                      <span class="stat-value">{{ uploadResult.errors.length || 0 }}</span>
                      <span class="stat-label">Errors</span>
                    </div>
                  </div>
                </div>
                <button class="btn-result-action" @click="clearUpload">
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT SECTION - SINGLE TENANT -->
      <div class="form-section">
        <!-- ADD TENANT CARD -->
        <div class="form-card">
          <div class="form-header">
            <div class="form-icon">
              <i class="bi bi-person-add"></i>
            </div>
            <div>
              <h4>Add Single Tenant</h4>
              <p>Quick manual entry</p>
            </div>
          </div>

          <form @submit.prevent="saveTenant">
            <div class="form-grid">
              <!-- FULL NAME -->
              <div class="form-field full-width">
                <label class="field-label">
                  <i class="bi bi-person"></i>
                  Full Name
                </label>
                <input v-model="tenant.tenant_name" class="form-input" placeholder="Ronald Test" required />
              </div>

              <!-- ID NUMBER -->
              <div class="form-field">
                <label class="field-label">
                  <i class="bi bi-card-text"></i>
                  ID Number
                </label>
                <input v-model="tenant.tenant_idnumber" class="form-input" placeholder="12345678" required />
              </div>

              <!-- PHONE -->
              <div class="form-field">
                <label class="field-label">
                  <i class="bi bi-phone"></i>
                  Phone
                </label>
                <div class="phone-input">
                  <span class="phone-prefix">+254</span>
                  <input v-model="tenant.tenant_phone" class="form-input" placeholder="712345678" required />
                </div>
              </div>

              <!-- GENDER -->
              <div class="form-field">
                <label class="field-label">
                  <i class="bi bi-gender-ambiguous"></i>
                  Gender
                </label>
                <div class="gender-options">
                  <button type="button" class="gender-option" 
                          :class="{ active: tenant.gender === 'Male' }"
                          @click="tenant.gender = 'Male'">
                    <i class="bi bi-gender-male"></i>
                    Male
                  </button>
                  <button type="button" class="gender-option" 
                          :class="{ active: tenant.gender === 'Female' }"
                          @click="tenant.gender = 'Female'">
                    <i class="bi bi-gender-female"></i>
                    Female
                  </button>
                </div>
              </div>

              <!-- MARITAL STATUS -->
              <div class="form-field">
                <label class="field-label">
                  <i class="bi bi-heart"></i>
                 Status
                </label>
                <div class="status-select">
                  <select v-model="tenant.marital_status" class="form-select">
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                  </select>
                </div>
              </div>

              <!-- EMAIL -->
          
            </div>

            <div class="form-actions">
              <button class="btn-save-tenant" type="submit">
                <i class="bi bi-check-lg"></i>
                Save Tenant
              </button>
              <button type="button" class="btn-clear-secondary" @click="clearForm">
                Clear
              </button>
            </div>
          </form>
        </div>

        <!-- STATS CARD -->
        <div class="stats-card">
          <div class="stats-header">
            <h5><i class="bi bi-graph-up"></i> Overview</h5>
          </div>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-icon-wrapper">
                <i class="bi bi-people"></i>
              </div>
              <div class="stat-details">
                <div class="stat-number">{{ tenantStats.total || 0 }}</div>
                <div class="stat-title">Total Tenants</div>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon-wrapper male">
                <i class="bi bi-gender-male"></i>
              </div>
              <div class="stat-details">
                <div class="stat-number">{{ tenantStats.male || 0 }}</div>
                <div class="stat-title">Male</div>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon-wrapper female">
                <i class="bi bi-gender-female"></i>
              </div>
              <div class="stat-details">
                <div class="stat-number">{{ tenantStats.female || 0 }}</div>
                <div class="stat-title">Female</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";
import Papa from 'papaparse';

export default {
  data() {
    return {
      file: null,
      filePreview: [],
      loading: false,
      uploadResult: null,
      tenantStats: {},
      tenant: {
        tenant_name: "",
        tenant_phone: "",
        tenant_idnumber: "",
        gender: "",
        marital_status: "",
      
      }
    };
  },

  async mounted() {
    await this.loadTenantStats();
  },

  methods: {
    async loadTenantStats() {
      try {
        const res = await apiFetch("/tenants");
        // const data = await res.json();
        this.tenantStats = res.data ||[];
      } catch (err) {
        console.error("Failed to load tenant stats:", err);
      }
    },

    async downloadTemplate() {
      try {
        const headers = ['tenant_name', 'tenant_phone', 'tenant_idnumber', 'gender',  'marital_status'];
        const sampleData = [
          ['Ronnie test', '0725029795', '212233223', 'Male', 'Single'],
        
        ];
        
        let csvContent = headers.join(',') + '\n';
        sampleData.forEach(row => {
          csvContent += row.join(',') + '\n';
        });
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        
        const a = document.createElement("a");
        a.href = url;
        a.download = "tenants_template.csv";
        document.body.appendChild(a);
        a.click();
        
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        this.showToast("success", "Template downloaded!");
        
      } catch (err) {
        console.error("Template download error:", err);
        this.showToast("error", "Download failed");
      }
    },

    handleFile(e) {
      const file = e.target.files[0];
      if (file) {
        this.file = file;
        this.previewFile(file);
      }
    },

    previewFile(file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          this.filePreview = results.data;
        },
        error: () => {
          this.showToast("error", "Invalid CSV format");
        }
      });
    },

    getRowCount() {
      return this.filePreview.length;
    },

    async uploadTenants() {
  if (!this.file) return;

  const formData = new FormData();
  formData.append("file", this.file);

  try {
    this.loading = true;

    const res = await apiFetch("/tenants/upload-csv", {
      method: "POST",
      body: formData
    });

    console.log("Upload response:", res);

    this.uploadResult = res;

    if (res.success) {
      this.showToast("success", `Added ${res.inserted} tenants`);
              this.$router.push("/tenants");


      await this.loadTenantStats();
    } else {
      throw new Error(res.message || "Upload failed");
    }

  } catch (err) {
    console.error("Upload tenants error:", err);
    this.showToast("error", err.message || "Upload failed");
  } finally {
    this.loading = false;
  }
},


    async saveTenant() {
  try {
    if (
      !this.tenant.tenant_name ||
      !this.tenant.tenant_idnumber ||
      !this.tenant.tenant_phone
    ) {
      this.showToast("warning", "Fill required fields");
      return;
    }

    const res = await apiFetch("/tenants", {
      method: "POST",
      body: JSON.stringify(this.tenant)
    });

    // Optional business check (since backend sends success)
    if (res.success === false) {
      throw new Error(res.message || "Failed to save tenant");
    }

    this.showToast("success", "Tenant saved!");
    this.clearForm();
        this.$router.push("/tenants");


    await this.loadTenantStats();

  } catch (err) {
    console.error("Save tenant error:", err);
    this.showToast(
      "error",
      err.message || "Maybe tenant with that ID number already exists. Just assign them to a unit"
    );
  }
},


    clearForm() {
      this.tenant = {
        full_name: "",
        id_number: "",
        phone: "",
        email: "",
        gender: "",
        marital_status: "",
        address: "",
        date_of_birth: "",
        occupation: ""
      };
    },

    clearUpload() {
      this.file = null;
      this.filePreview = [];
      this.uploadResult = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    removeFile() {
      this.clearUpload();
    },

    handleDrop(e) {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files.length && files[0].name.endsWith('.csv')) {
        this.file = files[0];
        this.previewFile(this.file);
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
    },

    getGenderBadgeClass(gender) {
      if (!gender) return 'badge-neutral';
      return gender.toLowerCase() === 'male' ? 'badge-male' : 'badge-female';
    },

    showToast(type, message) {
      alert(`${type.toUpperCase()}: ${message}`);
    }
  }
};
</script>

<style scoped>
.page-wrapper {
  background: var(--dash-bg);
  min-height: 100vh;
}

.add-tenant-container {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 25px;
  padding: 25px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: calc(100vh - 70px);
}

/* LEFT SECTION STYLES */
.info-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.info-header {
  text-align: center;
  margin-bottom: 30px;
}

.icon-wrapper {
  width: 70px;
  height: 70px;
  background: var(--dash-primary-soft);
  border-radius: var(--dash-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: var(--dash-primary-ink);
}

.icon-wrapper i {
  font-size: 1.75rem;
  color: var(--dash-primary-ink);
}

.title {
  font-size: var(--dash-fs-h1);
  font-weight: 700;
  color: var(--dash-ink);
  margin-bottom: 8px;
}

.subtitle {
  color: var(--dash-muted);
  font-size: 16px;
  font-weight: 500;
}

/* TEMPLATE CARD */
.template-card {
  background: var(--dash-bg-alt);
  border-radius: var(--dash-radius-lg);
  padding: 20px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid var(--dash-line);
}

.template-icon {
  width: 50px;
  height: 50px;
  background: var(--dash-primary-soft);
  border-radius: var(--dash-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-primary-ink);
}

.template-icon i {
  font-size: 24px;
  color: var(--dash-primary-ink);
}

.template-content {
  flex: 1;
}

.template-content h5 {
  font-size: 18px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 5px 0;
}

.template-content p {
  color: var(--dash-muted);
  font-size: 14px;
  margin: 0;
}

/* DOWNLOAD BUTTON */
.btn-template-download {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: var(--dash-pill);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: var(--dash-shadow);
}

.btn-template-download:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-template-download:active {
  transform: translateY(0);
}

/* UPLOAD SECTION */
.upload-section {
  margin-top: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--dash-ink);
  margin-bottom: 15px;
}

.upload-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 25px;
  border: 1px solid var(--dash-line);
}

/* FILE DROP ZONE */
.file-drop-zone {
  border: 2px dashed var(--dash-line-strong);
  border-radius: var(--dash-radius);
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.file-drop-zone:hover {
  border-color: var(--dash-primary);
  background: var(--dash-bg-alt);
}

.drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.drop-icon {
  width: 60px;
  height: 60px;
  background: var(--dash-bg-alt);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-icon i {
  font-size: 1.75rem;
  color: var(--dash-muted);
}

.drop-text {
  font-size: 16px;
  color: var(--dash-text);
  margin: 0;
}

.browse-link {
  color: var(--dash-primary);
  font-weight: 600;
  text-decoration: underline;
}

.drop-hint {
  font-size: 14px;
  color: var(--dash-faint);
  margin: 5px 0 0 0;
}

/* FILE PREVIEW */
.file-preview-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: var(--dash-bg-alt);
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
}

.file-icon {
  width: 40px;
  height: 40px;
  background: var(--dash-success-soft);
  border-radius: var(--dash-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-success-ink);
}

.file-icon i {
  font-size: 20px;
  color: var(--dash-success-ink);
}

.file-details {
  flex: 1;
}

.file-name {
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
  font-size: 15px;
}

.file-info {
  color: var(--dash-muted);
  font-size: 13px;
  margin: 4px 0 0 0;
}

.btn-remove-file {
  background: none;
  border: none;
  color: var(--dash-danger);
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: var(--dash-pill);
  transition: all 0.2s ease;
}

.btn-remove-file:hover {
  background: var(--dash-danger-soft);
}

/* PREVIEW SECTION */
.preview-section {
  margin: 20px 0;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--dash-text);
  font-weight: 600;
  margin-bottom: 15px;
  font-size: 14px;
}

.preview-table {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  overflow: hidden;
  border: 1px solid var(--dash-line);
}

.preview-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  padding: 15px;
  border-bottom: 1px solid var(--dash-line);
}

.preview-row:last-child {
  border-bottom: none;
}

.preview-cell {
  display: flex;
  flex-direction: column;
}

.cell-label {
  font-size: 11px;
  color: var(--dash-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.cell-value {
  font-size: 13px;
  color: var(--dash-ink);
  font-weight: 500;
}

/* BADGES */
.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-neutral {
  background: var(--dash-line);
  color: var(--dash-text);
}

.badge-male {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.badge-female {
  background: var(--dash-alt-soft);
  color: var(--dash-alt-ink);
}

/* PRIMARY UPLOAD BUTTON */
.btn-upload-primary {
  width: 100%;
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 18px;
  border-radius: var(--dash-pill);
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s ease;
  box-shadow: var(--dash-shadow);
  margin-top: 20px;
}

.btn-upload-primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: var(--dash-shadow);
}

.btn-upload-primary:active:not(:disabled) {
  transform: translateY(-1px);
}

.btn-upload-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* RESULTS */
.results-container {
  margin-top: 20px;
}

.result-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  border-radius: var(--dash-radius-lg);
  background: var(--dash-surface-2);
  border: 1px solid var(--dash-success-soft);
}

.result-card.success {
  background: var(--dash-success-soft);
  border-color: var(--dash-success-soft);
}

.result-card.error {
  background: var(--dash-danger-soft);
  border-color: var(--dash-danger-soft);
}

.result-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.result-card.success .result-icon {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.result-card.error .result-icon {
  background: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

.result-icon i {
  font-size: 24px;
  color: white;
}

.result-content {
  flex: 1;
}

.result-content h6 {
  font-size: 16px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 10px 0;
}

.result-stats {
  display: flex;
  gap: 15px;
}

.stat-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  background: white;
  border-radius: var(--dash-radius-sm);
  min-width: 70px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--dash-ink);
}

.stat-label {
  font-size: 12px;
  color: var(--dash-muted);
  margin-top: 2px;
}

.btn-result-action {
  background: white;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-pill);
  padding: 10px;
  color: var(--dash-text);
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-result-action:hover {
  background: var(--dash-surface-2);
  transform: rotate(180deg);
}

/* RIGHT SECTION STYLES */
.form-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  margin-bottom: 25px;
}

.form-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
}

.form-icon {
  width: 50px;
  height: 50px;
  background: var(--dash-warning-soft);
  border-radius: var(--dash-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-warning-ink);
}

.form-icon i {
  font-size: 24px;
  color: var(--dash-warning-ink);
}

.form-header h4 {
  font-size: 22px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0;
}

.form-header p {
  color: var(--dash-muted);
  font-size: 14px;
  margin: 4px 0 0 0;
}

/* FORM GRID */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.full-width {
  grid-column: span 2;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--dash-text);
  margin-bottom: 8px;
}

.field-label i {
  color: var(--dash-muted);
}

.form-input {
  padding: 12px 16px;
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius);
  font-size: 15px;
  transition: all 0.3s ease;
  background: var(--dash-surface-2);
}

.form-input:focus {
  outline: none;
  border-color: var(--dash-primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.phone-input {
  position: relative;
}

.phone-prefix {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-muted);
  font-weight: 500;
}

.phone-input .form-input {
  padding-left: 60px;
}

/* GENDER OPTIONS */
.gender-options {
  display: flex;
  gap: 10px;
}

.gender-option {
  flex: 1;
  padding: 12px;
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius-sm);
  background: var(--dash-surface-2);
  color: var(--dash-text);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.gender-option:hover {
  border-color: var(--dash-line-strong);
}

.gender-option.active {
  border-color: var(--dash-primary-ink);
  background: var(--dash-primary-soft);
  color: var(--dash-primary-hover);
}

/* STATUS SELECT */
.status-select select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius);
  font-size: 15px;
  background: var(--dash-surface-2);
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-select select:focus {
  outline: none;
  border-color: var(--dash-primary);
  background: white;
}

/* FORM ACTIONS */
.form-actions {
  display: flex;
  gap: 12px;
}

.btn-save-tenant {
  flex: 1;
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 16px;
  border-radius: var(--dash-pill);
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: var(--dash-shadow);
}

.btn-save-tenant:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-save-tenant:active {
  transform: translateY(0);
}

.btn-clear-secondary {
  padding: 16px 24px;
  background: var(--dash-line);
  color: var(--dash-text);
  border: none;
  border-radius: var(--dash-pill);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-clear-secondary:hover {
  background: var(--dash-line-strong);
}

/* STATS CARD */
.stats-card {
  background: var(--dash-primary);
  border-radius: var(--dash-radius-lg);
  padding: 25px;
  color: white;
  box-shadow: var(--dash-shadow);
}

.stats-header h5 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.stat-item {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: var(--dash-radius);
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon-wrapper {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--dash-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon-wrapper.male {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.stat-icon-wrapper.female {
  background: var(--dash-alt-soft);
  color: var(--dash-alt-ink);
}

.stat-icon-wrapper i {
  font-size: 20px;
}

.stat-details {
  flex: 1;
}

.stat-number {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 2px;
}

.stat-title {
  font-size: 12px;
  opacity: 0.9;
}

/* RESPONSIVE DESIGN */
@media (max-width: 1200px) {
  .add-tenant-container {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 20px;
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .form-field.full-width {
    grid-column: span 1;
  }
  
  .preview-row {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .template-card {
    flex-direction: column;
    text-align: center;
  }
  
  .result-stats {
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .preview-row {
    grid-template-columns: 1fr;
  }
  
  .gender-options {
    flex-direction: column;
  }
  
  .form-actions {
    flex-direction: column;
  }
}
</style>